<template>
  <div class="contact-page">
    <v-container>
      <!-- Page Title -->
      <v-row class="mb-6">
        <v-col cols="12">
          <h1 class="text-h3 font-weight-bold text-center text-accent mb-2">Contact</h1>
          <div class="text-center">
            <v-divider class="mb-4 mx-auto" width="100" thickness="3" color="primary"></v-divider>
          </div>
          <p class="text-center text-body-1 mx-auto" style="max-width: 800px">
            Hier vindt u contactgegevens van de Nederlands Gereformeerde kerk Eemdijk. Voor vragen
            of informatie kunt u contact opnemen met de relevante personen of instanties.
          </p>
        </v-col>
      </v-row>
      <!-- Church Building Card -->
      <v-row class="mb-8">
        <v-col cols="12">
          <v-card elevation="3" class="overflow-hidden">
            <v-row class="flex-column-reverse flex-md-row" no-gutters>
              <!-- Church Image Section -->
              <v-col cols="12" md="5">
                <v-img
                  src="/images/kerkBuiten.jpeg"
                  height="100%"
                  cover
                  class="h-100"
                  alt="Kerkgebouw NGK Eemdijk"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height" align="center" justify="center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-col>

              <!-- Church Info Section -->
              <v-col cols="12" md="7">
                <div class="pa-6">
                  <h2 class="text-h4 text-accent mb-4 d-flex align-center">
                    <v-icon size="x-large" color="primary" class="me-3">mdi-church</v-icon>
                    Kerkgebouw
                  </h2>

                  <div class="d-flex flex-column flex-sm-row gap-8 mb-6">
                    <!-- Address -->
                    <div class="flex-grow-1 mb-4 mb-sm-0">
                      <div class="text-h6 mb-2 font-weight-medium">Westerkerk</div>
                      <div class="d-flex mb-2">
                        <v-icon size="small" color="primary" class="me-2 mt-1"
                          >mdi-map-marker</v-icon
                        >
                        <div>
                          <div>Eemdijk 63</div>
                          <div>3754 ND Eemdijk</div>
                        </div>
                      </div>
                      <div class="d-flex mb-2">
                        <v-icon size="small" color="primary" class="me-2 mt-1">mdi-phone</v-icon>
                        <span>033-2988572</span>
                      </div>
                    </div>

                    <!-- Service Times -->
                    <div class="flex-grow-1">
                      <div class="text-h6 mb-2 font-weight-medium">Diensten</div>
                      <div class="d-flex mb-2">
                        <v-icon size="small" color="primary" class="me-2 mt-1">mdi-calendar</v-icon>
                        <div>
                          <div>{{ sundayServiceText }}</div>
                        </div>
                      </div>
                      <v-btn
                        color="primary"
                        variant="tonal"
                        size="small"
                        class="mt-2"
                        prepend-icon="mdi-calendar-month"
                        to="/kerkdiensten"
                      >
                        Bekijk kerkdiensten
                      </v-btn>
                    </div>
                  </div>

                  <!-- Google Map -->
                  <div class="map-container" style="border-radius: 8px; overflow: hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2438.5347986756664!2d5.346973351293539!3d52.270825258169196!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c64717dcb57507%3A0xb43fd1e990a48d46!2sEemdijk%2063%2C%203754%20ND%20Eemdijk!5e0!3m2!1sen!2snl!4v1653574359012!5m2!1sen!2snl"
                      width="100%"
                      height="250"
                      style="border: 0"
                      allowfullscreen="true"
                      loading="lazy"
                      referrerpolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>

                  <div class="mt-4 text-right">
                    <v-btn
                      prepend-icon="mdi-directions"
                      color="primary"
                      variant="outlined"
                      target="_blank"
                      href="https://www.google.com/maps/dir/?api=1&destination=Eemdijk+63,+3754+ND+Eemdijk,+Netherlands"
                    >
                      Routebeschrijving
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Contact Directory -->
      <v-row class="mb-8">
        <v-col cols="12">
          <div class="contact-directory-intro">
            <h2 class="text-h4 text-accent mb-3">Contactpersonen</h2>
            <p class="text-body-1 mb-0 contact-directory-intro__copy">
              Hieronder staan de contactpersonen gegroepeerd per onderwerp in een eenvoudig
              overzicht. Zo kunt u snel scannen zonder extra interactie.
            </p>
          </div>
        </v-col>

        <v-col v-for="group in directoryGroups" :key="group.value" cols="12" md="6">
          <section class="contact-group-section">
            <div class="contact-group-title mb-4">
              <div>
                <div class="text-h6 text-primary">{{ group.label }}</div>
                <div class="text-body-2">{{ groupDescriptions[group.value] }}</div>
              </div>
              <div class="contact-group-count">
                {{ contactsByGroup[group.value].length }} contacten
              </div>
            </div>

            <v-expansion-panels class="contact-group-list" variant="accordion">
              <v-expansion-panel
                v-for="contact in contactsByGroup[group.value]"
                :key="contact.title"
                class="contact-directory-item"
                rounded="lg"
                elevation="0"
              >
                <v-expansion-panel-title class="contact-directory-item__title">
                  <div class="contact-directory-item__top">
                    <div class="d-flex align-start ga-3">
                      <v-icon :icon="contact.icon" color="accent" size="22" class="mt-1"></v-icon>
                      <div>
                        <h3 class="text-h6 text-accent mb-1">{{ contact.title }}</h3>
                        <p class="text-body-2 mb-0">{{ contact.description }}</p>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <div class="contact-directory-item__meta">
                    <div>
                      <div class="contact-directory-item__label">Contactpersoon</div>
                      <div class="font-weight-medium">{{ contact.name }}</div>
                      <div v-if="contact.note" class="text-body-2">{{ contact.note }}</div>
                    </div>

                    <div>
                      <div class="contact-directory-item__label">Gegevens</div>
                      <div class="contact-details d-flex flex-column ga-2">
                        <div v-if="contact.addressLines.length" class="d-flex align-start ga-2">
                          <v-icon size="small" color="highlight" class="mt-1"
                            >mdi-map-marker</v-icon
                          >
                          <div>
                            <div v-for="line in contact.addressLines" :key="line">{{ line }}</div>
                          </div>
                        </div>
                        <div v-if="contact.phone" class="d-flex align-start ga-2">
                          <v-icon size="small" color="highlight" class="mt-1">mdi-phone</v-icon>
                          <span>{{ contact.phone }}</span>
                        </div>
                        <div class="d-flex align-start ga-2">
                          <v-icon size="small" color="highlight" class="mt-1">mdi-email</v-icon>
                          <a :href="`mailto:${contact.email}`" class="text-decoration-none">
                            {{ contact.email }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getSundayServiceText } from '../../shared/serviceTimes';

type ContactGroup = 'general' | 'care' | 'services' | 'practical';

const sundayServiceText = getSundayServiceText();

type ContactItem = {
  title: string;
  icon: string;
  description: string;
  name: string;
  note?: string;
  addressLines: string[];
  phone?: string;
  email: string;
  group: ContactGroup;
};

const contactGroups = [
  { value: 'general' as const, label: 'Algemeen' },
  { value: 'care' as const, label: 'Zorg en geloof' },
  { value: 'services' as const, label: 'Diensten' },
  { value: 'practical' as const, label: 'Praktisch' }
];

const groupDescriptions: Record<ContactGroup, string> = {
  general: 'Algemene communicatie en gemeentezaken.',
  care: 'Pastoraat, hulpvragen en ondersteuning.',
  services: 'Kerkdiensten, liturgie en meeluisteren.',
  practical: 'Gebouw, administratie en praktische afstemming.'
};

const contacts: ContactItem[] = [
  {
    title: 'Kerkelijk bureau',
    icon: 'mdi-account',
    description: 'Voor algemene informatie, correspondentie en praktische vragen aan de gemeente.',
    name: 'mw. C. Koelewijn',
    addressLines: ['Kluut 25', '3752 PR Bunschoten'],
    email: 'scriba@ngk-eemdijk.nl',
    group: 'general'
  },
  {
    title: 'Predikant',
    icon: 'mdi-account-tie',
    description: 'Voor geloofsgesprekken, pastoraat en persoonlijke vragen rond de gemeente.',
    name: 'ds. A.M. Clement',
    addressLines: ['Eemdijk 65', '3754 ND Eemdijk'],
    email: 'dominee@ngk-eemdijk.nl',
    group: 'care'
  },
  {
    title: 'Diaconie',
    icon: 'mdi-hand-heart',
    description:
      'Voor hulpvragen, omzien naar elkaar en diaconale ondersteuning binnen en buiten de gemeente.',
    name: 'Diaconie NGK Eemdijk',
    addressLines: ['Eemdijk 63', '3754 ND Eemdijk'],
    email: 'diaconie@ngk-eemdijk.nl',
    group: 'care'
  },
  {
    title: 'Preekvoorziening',
    icon: 'mdi-calendar-text',
    description: 'Voor vragen rondom voorgangers, roosterzaken en de planning van diensten.',
    name: 'Klaas van Dijk',
    addressLines: ['Eemdijk 65', '3754 ND Eemdijk'],
    email: 'preekvoorziening@ngk-eemdijk.nl',
    group: 'services'
  },
  {
    title: 'Koster',
    icon: 'mdi-home',
    description: 'Voor het kerkgebouw, toegang, praktische afstemming en gebruik van ruimtes.',
    name: 'D.J. van Twillert',
    addressLines: ['Eemdijk 40a', '3754 NJ Eemdijk'],
    phone: '033-2986055',
    email: 'd.vantwillert@ngk-eemdijk.nl',
    group: 'practical'
  },
  {
    title: 'Beamerteam',
    icon: 'mdi-video',
    description: 'Voor vragen over beamer, presentatie en het meeluisteren van diensten.',
    name: 'Dominique Nagel',
    addressLines: [],
    email: 'beamerteameemdijk@ngk-eemdijk.nl',
    group: 'services'
  },
  {
    title: 'Boekhouder',
    icon: 'mdi-calculator',
    description: 'Voor financiele administratie, betalingen en boekhoudkundige vragen.',
    name: 'P.F. Koops',
    addressLines: ['Kerkepad 22', '3754 NR Eemdijk'],
    phone: '033-2986061',
    email: 'boekhouder@ngk-eemdijk.nl',
    group: 'practical'
  },
  {
    title: 'Liturgie',
    icon: 'mdi-music',
    description: 'Voor vragen over muziek en de liturgische organisatie van diensten.',
    name: 'Organisten, Koster',
    addressLines: [],
    email: 'liturgie@ngk-eemdijk.nl',
    group: 'services'
  },
  {
    title: 'Webmaster',
    icon: 'mdi-web',
    description: 'Voor verbeteringen, foutmeldingen of inhoudelijke vragen over de website.',
    name: 'Webmaster NGK Eemdijk',
    note: 'Technische en inhoudelijke vragen over de website.',
    addressLines: [],
    email: 'webmaster@ngk-eemdijk.nl',
    group: 'general'
  }
];

const directoryGroups = contactGroups;
const contactsByGroup = computed<Record<ContactGroup, ContactItem[]>>(() => ({
  general: contacts.filter((contact) => contact.group === 'general'),
  care: contacts.filter((contact) => contact.group === 'care'),
  services: contacts.filter((contact) => contact.group === 'services'),
  practical: contacts.filter((contact) => contact.group === 'practical')
}));
</script>

<style scoped>
.contact-page {
  padding-bottom: 2rem;
}

.contact-directory-intro__copy {
  max-width: 48rem;
}

.contact-group-section {
  height: 100%;
  padding: 1.25rem;
  border: 1px solid rgba(80, 86, 143, 0.12);
  border-radius: 20px;
  background: #ffffff;
}

.contact-group-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.contact-group-count {
  color: rgba(80, 86, 143, 0.82);
  font-size: 0.9rem;
  white-space: nowrap;
}

.contact-group-list {
  display: grid;
  gap: 0.75rem;
}

.contact-group-list :deep(.v-expansion-panel) {
  border: 1px solid rgba(80, 86, 143, 0.12);
  background: #ffffff;
}

.contact-group-list :deep(.v-expansion-panel-title) {
  padding: 1rem 1rem 0.95rem;
}

.contact-directory-item__top {
  width: 100%;
}

.contact-directory-item__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  padding-top: 0.25rem;
}

.contact-directory-item__label {
  margin-bottom: 0.45rem;
  color: rgba(80, 86, 143, 0.82);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.text-kicker {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.map-container {
  border-radius: 8px;
  overflow: hidden;
}

.contact-details a {
  color: inherit;
}

.contact-details a:hover {
  color: var(--v-primary-base);
}

@media (max-width: 960px) {
  .contact-directory-item__meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .contact-group-title {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
